import type { SelectOptionType } from '../../Controls/Select/types';
import type {
  DataSource,
  DependsOnEntry,
  FilterBarController,
  FilterBarOperation,
  FilterBarPersistenceController,
  FilterBarSnapshot,
  FilterFieldPhase,
  FilterItemConfig,
  FilterValues,
  UseFilterBarConfig,
} from '../types';
import { validateItems } from './invariants';
import { staticOptionsEqual } from './options-compare';
import {
  buildGraph,
  getDescendants,
  getDirectDependents,
  type DependencyGraph,
} from './dependency-graph';
import { FetchRegistry } from './fetch-registry';
import { OptionsCache } from './options-cache';
import {
  canonicalEmpty,
  convertOptionBackedValue,
  currentValues,
  initialState,
  reducer,
  type Action,
  type State,
} from './reducer';
import {
  emitIfReady,
  shallowEqualValue,
  shallowEqualValues,
} from './init-sequencer';

type OrchestratorOptions = {
  source: 'cascade' | 'imperative' | 'user';
  trigger?: string;
  operation?: FilterBarOperation;
  /** When true, skip running cascade — used by setMany({ cascade: false }). */
  skipCascade?: boolean;
};

type SnapshotListener<T extends FilterValues> = (
  next: FilterBarSnapshot<T>,
  prev: FilterBarSnapshot<T>,
) => void;

/**
 * Circuit breaker for content-unstable static options. A field whose options
 * content changes on this many CONSECUTIVE reconcile passes is treated as
 * producing volatile content (random keys, timestamps, values derived from
 * render state) — propagating would loop dispatch → render → rebuild →
 * dispatch forever, so propagation for the field pauses instead.
 *
 * Consumers that rebuild `items` inline can't trip this: the propagation's
 * own re-renders interleave content-stable passes, which reset the counter.
 * A consumer that memoizes the whole `items` array only produces passes when
 * content genuinely changes, so a burst of more than LIMIT back-to-back list
 * updates pauses the field transiently — the pending list still lands on the
 * first content-stable pass, because propagation is decided against the
 * last-PROPAGATED snapshot, not the last-seen one.
 */
const STATIC_OPTIONS_CHURN_LIMIT = 5;

/**
 * The FilterBar state machine as an external store.
 *
 * One instance is created per `useFilterBar` call and lives for the hook's
 * lifetime — the instance IS the public `FilterBarController`: methods are
 * identity-stable, reactive data flows through `getState()` + `subscribe()`
 * (bound to React via `useSyncExternalStore` in the companion hooks).
 *
 * Architecture (Plan + the store refactor):
 *   - `dispatchAction` reduces synchronously, swaps `state`, rebuilds the
 *     public snapshot (identity-preserving per slot), notifies listeners, then
 *     runs the emission pipeline (`onInit` / `onChange` / `onFieldChange`,
 *     driven by the same counters as before) and auto-save scheduling.
 *     Transactions close in async orchestrator code, so consumer events never
 *     fire during a React render.
 *   - The orchestrator (init, cascades, operations) is moved verbatim from
 *     the pre-store hook; it reads `this.state` between awaits exactly like
 *     the old `stateRef.current`.
 *   - `start()` / `stop()` make init restart-safe: StrictMode's
 *     mount → cleanup → mount aborts in-flight fetches on cleanup and
 *     re-enters init on the second mount (the OptionsCache absorbs refetches).
 *   - The React hook syncs the freshest config every render via
 *     `syncConfig` (the old refs pattern), and drives `reconcileItems` /
 *     `applyControlled` from effects.
 */
export class FilterBarStore<T extends FilterValues = FilterValues>
implements FilterBarController<T> {
  // ---------- Config (synced every render by the hook) ----------
  private config: UseFilterBarConfig<T>;

  private itemsProp: FilterItemConfig[];

  private itemsByNameProp: Record<string, FilterItemConfig>;

  // ---------- State machine ----------
  private state: State;

  private snapshot: FilterBarSnapshot<T>;

  private listeners = new Set<SnapshotListener<T>>();

  private graph: DependencyGraph;

  private registry = new FetchRegistry();

  private cache = new OptionsCache();

  private pendingTxn = 0;

  /** Mirrors "onInit has been emitted" — the emission gate for onChange. */
  private initializedRef = { current: false };

  // ---------- Init lifecycle ----------
  private started = false;

  private initRunId = 0;

  // ---------- Persistence ----------
  private lastPersisted: FilterValues | null = null;

  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  private persistenceApi: FilterBarPersistenceController | null = null;

  // ---------- Items reconciliation bookkeeping (churn breaker) ----------
  private itemsSignature: string;

  // Two snapshots per field: `lastSeen` advances on EVERY pass and drives
  // churn detection; `lastPropagated` advances only when a change actually
  // propagates and drives the propagate decision. The split is what makes a
  // breaker trip recoverable — a pending list skipped while tripped is still
  // different from `lastPropagated`, so it lands on the first stable pass.
  private lastSeenOptions: Map<string, SelectOptionType[]>;

  private lastPropagatedOptions: Map<string, SelectOptionType[]>;

  private optionsChurn = new Map<string, number>();

  private churnTripped = new Set<string>();

  constructor(config: UseFilterBarConfig<T>) {
    this.config = config;
    this.itemsProp = config.items;
    this.itemsByNameProp = indexByName(config.items);
    // Throws synchronously on invalid config / cyclic deps — the store is
    // constructed during render, preserving the old fail-at-mount contract.
    validateItems(config.items);
    this.graph = buildGraph(config.items);
    this.state = initialState(config.items);
    this.itemsSignature = computeItemsSignature(config.items);
    this.lastSeenOptions = collectStaticOptions(config.items);
    this.lastPropagatedOptions = new Map(this.lastSeenOptions);
    this.snapshot = this.buildSnapshot(null);
  }

  // ==========================================================================
  // Hook-facing lifecycle (not part of the public controller contract)
  // ==========================================================================

  /**
   * Refs-style config sync — called during every hook render so option loads,
   * defaults, cascades and event callbacks always run against the CURRENT
   * closures (the reducer's itemsByName refreshes only on reconcile).
   */
  syncConfig(config: UseFilterBarConfig<T>): void {
    const itemsChanged = config.items !== this.itemsProp;
    this.config = config;
    this.itemsProp = config.items;
    if (itemsChanged) {
      this.itemsByNameProp = indexByName(config.items);
    }
  }

  /**
   * Restart-safe init. First call runs the full init sequence; a `stop()` →
   * `start()` pair (StrictMode remount) aborts the in-flight run and re-enters
   * init from scratch; once initialized, further calls are no-ops.
   */
  start = (): void => {
    this.started = true;
    if (this.state.initialized) return;
    const runId = ++this.initRunId;
    this.runInit(runId).catch((err) => {
      console.warn('[FilterBar] init failed:', err);
    });
  };

  stop = (): void => {
    this.started = false;
    this.registry.abortAll();
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  };

  /**
   * Items reconciliation — driven by the hook's `[items]` effect (after first
   * render; the constructor owns the baseline). Plan: "items array reference
   * stability not required" — a bare reference change with equal content must
   * stay a no-op. Two kinds of change reconcile:
   *   - structural (name/type/dependsOn signature) → RECONCILE_ITEMS + init new fields;
   *   - static options content → RECONCILE_ITEMS + re-read + stale-value handling.
   * Content-UNSTABLE options pause per-field propagation via the churn breaker.
   */
  reconcileItems = (items: FilterItemConfig[]): void => {
    const signature = computeItemsSignature(items);
    const currentStatic = collectStaticOptions(items);
    const structuralChange = signature !== this.itemsSignature;

    const changedOptions: string[] = [];
    for (const [name, current] of currentStatic) {
      const propagated = this.lastPropagatedOptions.get(name);
      if (propagated === undefined) {
        // First sight (new field / dataSource became static) — baseline it;
        // the structural path owns its initialization.
        this.lastPropagatedOptions.set(name, current);
        continue;
      }
      const seen = this.lastSeenOptions.get(name);
      if (seen !== undefined && staticOptionsEqual(seen, current)) {
        // Stable pass — reset the churn counter and re-arm a tripped field.
        this.optionsChurn.set(name, 0);
        this.churnTripped.delete(name);
      } else {
        const churn = (this.optionsChurn.get(name) ?? 0) + 1;
        this.optionsChurn.set(name, churn);
        if (churn > STATIC_OPTIONS_CHURN_LIMIT && !this.churnTripped.has(name)) {
          this.churnTripped.add(name);
          console.error(
            `[FilterBar] Static options for field "${name}" changed content on ${churn} `
            + 'consecutive renders — the options array looks content-unstable (volatile keys, '
            + 'timestamps, or values derived from render state). Options propagation for this '
            + 'field is paused until its content stabilizes; memoize the array or remove the '
            + 'volatile props.',
          );
        }
      }
      if (this.churnTripped.has(name)) continue;
      if (staticOptionsEqual(propagated, current)) continue;
      changedOptions.push(name);
      this.lastPropagatedOptions.set(name, current);
    }
    this.lastSeenOptions = currentStatic;
    // Prune bookkeeping for names that are gone (or no longer static).
    for (const name of Array.from(this.lastPropagatedOptions.keys())) {
      if (!currentStatic.has(name)) {
        this.lastPropagatedOptions.delete(name);
        this.optionsChurn.delete(name);
        this.churnTripped.delete(name);
      }
    }

    if (!structuralChange && changedOptions.length === 0) return;

    this.itemsSignature = signature;
    validateItems(items);
    const previouslyKnown = new Set(this.state.itemsOrder);

    const refreshNames = new Set(changedOptions);
    let firstTypeChange: string | null = null;
    if (structuralChange) {
      // The graph depends only on names + dependsOn, both covered by the
      // structural signature — rebuild exactly when it can differ.
      this.graph = buildGraph(items);
      // Removed names: abort in-flight fetches and drop cached options
      // (plan: "removed name → in-flight requests aborted").
      const nextNames = new Set(items.map((i) => i.name));
      for (const name of previouslyKnown) {
        if (!nextNames.has(name)) {
          this.registry.abort(name);
          this.cache.invalidate(name);
        }
      }
      // Type changes: the reducer converts values inside the option-backed
      // family (re-validated via the refresh below) and purges every other
      // pair — purged fields drop out of `previouslyKnown` so they re-init
      // through the full default chain like a newly added field.
      const prevByName = this.state.itemsByName;
      for (const item of items) {
        const prev = prevByName[item.name];
        if (!prev || prev.type === item.type) continue;
        firstTypeChange = firstTypeChange ?? item.name;
        if (convertOptionBackedValue(prev.type, item.type, null).converted) {
          refreshNames.add(item.name);
        } else {
          previouslyKnown.delete(item.name);
        }
      }
    }

    // Type changes rewrite values inside RECONCILE_ITEMS — open the
    // transaction FIRST so prevValuesAtTxnStart snapshots the true
    // pre-conversion values and the change surfaces through onChange with a
    // correct diff. Safe: a type change always queues follow-up work
    // (convert → refresh, purge → init), so the transaction is guaranteed
    // to close.
    if (firstTypeChange !== null) {
      this.ensureTxnOpen('imperative', firstTypeChange, 'itemsReconcile');
    }

    this.dispatchAction({ type: 'RECONCILE_ITEMS', items });
    if (structuralChange) this.queueReconcileInit(items, previouslyKnown);
    this.queueOptionsRefresh(Array.from(refreshNames));
  };

  /** Controlled-mode prop sync — absorbs `values` prop diffs echo-silently. */
  applyControlled = (controlled: T): void => {
    if (!this.initializedRef.current) return;
    const internal = currentValues(this.state);
    if (shallowEqualValues(controlled as FilterValues, internal)) return;
    // Apply each diff without opening a transaction (echo silence).
    for (const k of Object.keys(controlled as FilterValues)) {
      const v = (controlled as FilterValues)[k];
      if (!shallowEqualValue(v, internal[k])) {
        this.dispatchAction({ type: 'FIELD_SET_VALUE', name: k, value: v, userTouched: false });
      }
    }
  };

  /** Internal reducer state — for the library's own components (per-field subscriptions). */
  getInternalState = (): State => this.state;

  // ==========================================================================
  // Public controller API — snapshot reads
  // ==========================================================================

  getState = (): FilterBarSnapshot<T> => this.snapshot;

  getValues = (): T => this.snapshot.values;

  getItems = (): FilterItemConfig[] => this.itemsProp;

  getOptions = (name: keyof T & string): SelectOptionType[] =>
    this.state.fields[name]?.options ?? [];

  getSelectedOptions = (name: keyof T & string): SelectOptionType[] => {
    const field = this.state.fields[name];
    if (!field) return [];
    const opts = field.options;
    const val = field.value;
    if (val === null || val === undefined || val === '') return [];
    if (Array.isArray(val)) {
      return val
        .map((v) => findOptionInTree(opts, v))
        .filter((o): o is SelectOptionType => o !== undefined);
    }
    const found = findOptionInTree(opts, val);
    return found ? [found] : [];
  };

  findOption = (name: keyof T & string, value: unknown): SelectOptionType | undefined =>
    findOptionInTree(this.state.fields[name]?.options ?? [], value);

  getPhase = (name: keyof T & string): FilterFieldPhase =>
    this.state.fields[name]?.phase ?? 'IDLE';

  getError = (name: keyof T & string): string | null =>
    this.state.fields[name]?.error ?? null;

  isLoading = (name: keyof T & string): boolean => {
    const field = this.state.fields[name];
    if (!field) return false;
    return field.phase === 'LOADING_OPTIONS' || field.phase === 'LOADING_DEFAULT';
  };

  // ==========================================================================
  // Public controller API — reactivity
  // ==========================================================================

  subscribe = (listener: SnapshotListener<T>): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // ==========================================================================
  // Public controller API — imperative operations
  // ==========================================================================

  set = (name: keyof T & string, value: unknown): void => {
    this.txnIncrement();
    this.ensureTxnOpen('user', name, 'set');
    this.dispatchAction({ type: 'FIELD_SET_VALUE', name, value, userTouched: true });
    this.runCascadeFrom(name, { source: 'user', trigger: name, operation: 'set' })
      .finally(this.txnDecrement);
  };

  setMany = (patch: Partial<T>, opts: { cascade?: boolean } = {}): void => {
    const cascade = opts.cascade !== false;
    const source = 'imperative';
    const keys = Object.keys(patch);
    if (keys.length === 0) return;
    this.ensureTxnOpen(source, keys[0], 'setMany');
    for (const k of keys) {
      this.dispatchAction({
        type: 'FIELD_SET_VALUE',
        name: k,
        value: (patch as FilterValues)[k],
        userTouched: true,
      });
    }
    this.txnIncrement();
    if (cascade) {
      // Run cascade for each set field's dependents — patch keys themselves
      // are excluded from their own cascade (deep-link seeding depends on it).
      const allDependents = new Set<string>();
      for (const k of keys) {
        for (const d of getDescendants(this.graph, k)) {
          if (!keys.includes(d)) allDependents.add(d);
        }
      }
      if (allDependents.size > 0) {
        Promise.all(
          Array.from(allDependents).map((dep) => {
            const item = this.getItem(dep);
            const policy = item?.dependsOn?.find((d) => keys.includes(d.name))?.onParentChange ?? 'reset';
            return this.cascadeField(dep, policy);
          }),
        ).finally(this.txnDecrement);
      } else {
        Promise.resolve().finally(this.txnDecrement);
      }
    } else {
      Promise.resolve().finally(this.txnDecrement);
    }
  };

  clear = (name: keyof T & string): void => {
    if (!this.getItem(name)) return;
    this.runOperation('clear', [name], name, 'clear');
  };

  clearAll = (): void => {
    this.runOperation('clear', this.state.itemsOrder, undefined, 'clearAll');
  };

  reset = (name: keyof T & string): void => {
    if (!this.getItem(name)) return;
    this.runOperation('reset', [name], name, 'reset');
  };

  resetAll = (): void => {
    this.runOperation('reset', this.state.itemsOrder, undefined, 'resetAll');
  };

  reload = (name?: keyof T & string): void => {
    this.ensureTxnOpen('imperative', name, 'reload');
    this.txnIncrement();
    const names = name ? [name] : this.state.itemsOrder;
    Promise.all(
      names.map(async (n) => {
        this.cache.invalidate(n);
        const opts = await this.loadOptionsForField(n, { useCache: false });
        if (opts) await this.handleStaleValueAndDefault(n, opts);
      }),
    ).finally(this.txnDecrement);
  };

  addField = (name: keyof T & string): void => {
    const item = this.getItem(name);
    if (!item) return;
    this.ensureTxnOpen('imperative', name, 'addField');
    this.dispatchAction({ type: 'ADD_FIELD', name });
    // Re-init this field — full default → getDefaultValue chain.
    this.txnIncrement();
    (async () => {
      if (item.defaultValue !== undefined) {
        this.dispatchAction({ type: 'FIELD_SET_VALUE', name, value: item.defaultValue, userTouched: false });
      } else {
        this.dispatchAction({ type: 'FIELD_SET_VALUE', name, value: canonicalEmpty(item), userTouched: false });
      }
      const opts = await this.loadOptionsForField(name);
      if (opts) await this.handleStaleValueAndDefault(name, opts);
    })().finally(this.txnDecrement);
  };

  removeField = (name: keyof T & string): void => {
    this.txnIncrement();
    this.ensureTxnOpen('imperative', name, 'removeField');
    this.dispatchAction({ type: 'REMOVE_FIELD', name });
    this.runCascadeFrom(name, { source: 'imperative', trigger: name, operation: 'removeField' })
      .finally(this.txnDecrement);
  };

  get persistence(): FilterBarPersistenceController | undefined {
    if (!this.config.persistence) return undefined;
    if (!this.persistenceApi) this.persistenceApi = this.buildPersistenceApi();
    return this.persistenceApi;
  }

  // ==========================================================================
  // Dispatch pipeline
  // ==========================================================================

  /**
   * Synchronous reduce → swap → snapshot → notify → emit. The orchestrator
   * reads `this.state` between awaits and sees post-dispatch state
   * immediately; listeners (React bindings) and event callbacks observe every
   * transition. Events fire after listener notification; transactions close
   * in async orchestrator code, so events never fire during a React render.
   */
  private dispatchAction = (action: Action): void => {
    const prevState = this.state;
    const nextState = reducer(prevState, action);
    if (nextState === prevState) return;
    this.state = nextState;
    const prevSnapshot = this.snapshot;
    this.snapshot = this.buildSnapshot(prevSnapshot);
    this.notify(prevSnapshot);
    this.afterTransition(prevState, nextState);
  };

  private notify(prevSnapshot: FilterBarSnapshot<T>): void {
    const next = this.snapshot;
    for (const listener of Array.from(this.listeners)) {
      listener(next, prevSnapshot);
    }
  }

  private afterTransition(prev: State, next: State): void {
    emitIfReady<T>(prev, next, {
      // Wrappers read the latest config so callback identity churn on the
      // consumer side is harmless (the old callbacksRef pattern).
      onInit: (values) => this.config.onInit?.(values),
      onChange: (values, meta) => this.config.onChange?.(values, meta),
      onFieldChange: this.config.onFieldChange,
      initializedRef: this.initializedRef,
    });
    if (prev.closedTxnCount !== next.closedTxnCount) {
      this.scheduleAutoSave();
    }
  }

  /**
   * Snapshot derivation. Slot identities are preserved while content is
   * unchanged (`values` / `phases` / `errors` / `visibleFields`), and the
   * snapshot object itself is reused when nothing visible changed — the
   * `useSyncExternalStore` contract (`getSnapshot` must be referentially
   * stable between transitions) plus cheap slot-level selection.
   */
  private buildSnapshot(prev: FilterBarSnapshot<T> | null): FilterBarSnapshot<T> {
    const s = this.state;
    const freshValues = currentValues(s) as T;
    const values = prev && shallowEqualValues(prev.values, freshValues)
      ? prev.values
      : freshValues;
    const phases = preserveRecordIdentity(
      prev?.phases,
      s.itemsOrder,
      (n) => s.fields[n]?.phase ?? 'IDLE',
    );
    const errors = preserveRecordIdentity(
      prev?.errors,
      s.itemsOrder,
      (n) => s.fields[n]?.error ?? null,
    );
    const isReady = s.initialized;
    const isDirty = this.computeIsDirty(freshValues);
    const isLoadingAny = s.itemsOrder.some((n) => {
      const f = s.fields[n];
      return Boolean(f) && (f.phase === 'LOADING_OPTIONS' || f.phase === 'LOADING_DEFAULT');
    });
    const { visibleFields } = s;
    if (
      prev
      && prev.values === values
      && prev.isReady === isReady
      && prev.isDirty === isDirty
      && prev.isLoadingAny === isLoadingAny
      && prev.visibleFields === visibleFields
      && prev.phases === phases
      && prev.errors === errors
    ) {
      return prev;
    }
    return { values, isReady, isDirty, isLoadingAny, visibleFields, phases, errors };
  }

  private computeIsDirty(values: FilterValues): boolean {
    if (!this.config.persistence) return false;
    if (!this.state.initialized) return false;
    return !shallowEqualValues(values, this.lastPersisted ?? {});
  }

  /** Rebuild + notify for derived-only changes (isDirty after save/load/clear). */
  private refreshDerived(): void {
    const prevSnapshot = this.snapshot;
    const next = this.buildSnapshot(prevSnapshot);
    if (next === prevSnapshot) return;
    this.snapshot = next;
    this.notify(prevSnapshot);
  }

  // ==========================================================================
  // Transactions
  // ==========================================================================

  /** Open a transaction if not already open (first opener's meta wins). */
  private ensureTxnOpen(
    source: 'cascade' | 'imperative' | 'user',
    trigger?: string,
    operation?: FilterBarOperation,
  ): void {
    if (!this.state.transactionOpen) {
      this.dispatchAction({ type: 'CASCADE_OPEN', source, trigger, operation });
    }
  }

  /** Increment txn pending — orchestrator unit of async work. */
  private txnIncrement = (): void => {
    this.pendingTxn += 1;
  };

  private txnDecrement = (): void => {
    this.pendingTxn -= 1;
    if (this.pendingTxn <= 0) {
      this.pendingTxn = 0;
      if (this.state.transactionOpen) {
        this.dispatchAction({ type: 'CASCADE_CLOSE' });
      }
    }
  };

  // ==========================================================================
  // Orchestrator (moved verbatim from the pre-store hook)
  // ==========================================================================

  // Prefer the freshest items prop; fall back to reducer state for fields
  // that are mid-removal (still in state, already gone from props).
  private getItem(name: string): FilterItemConfig | undefined {
    return this.itemsByNameProp[name] ?? this.state.itemsByName[name];
  }

  private getParentValues(name: string): FilterValues {
    const parents = this.graph.parentsOf.get(name) ?? new Set<string>();
    const cur = currentValues(this.state);
    const out: FilterValues = {};
    for (const p of parents) out[p] = cur[p];
    return out;
  }

  /**
   * Resolve a field's "baseline" value, uniform across all variants:
   *   1. static `defaultValue` (if set) — always wins.
   *   2. else `getDefaultValue` (if set) — runs against the field's options.
   *   3. else canonical empty.
   */
  private async resolveDefault(item: FilterItemConfig, options: SelectOptionType[]): Promise<unknown> {
    if (item.defaultValue !== undefined) return item.defaultValue;
    if (typeof item.getDefaultValue === 'function') {
      try {
        const cur = currentValues(this.state);
        const v = await item.getDefaultValue(options, cur);
        return v;
      } catch (err) {
        console.warn(`[FilterBar] getDefaultValue for "${item.name}" threw:`, err);
        return null;
      }
    }
    return canonicalEmpty(item);
  }

  /**
   * Load options for a single field (static OR async), respecting cacheKey short-circuit.
   * Returns the resolved options or null if discarded due to staleness/abort.
   */
  private async loadOptionsForField(
    name: string,
    opts: { useCache?: boolean } = {},
  ): Promise<SelectOptionType[] | null> {
    const item = this.getItem(name);
    if (!item) return null;

    // SELECT, SELECT_MULTI, SEGMENT_TOGGLE all carry a `dataSource`; other variants
    // (TEXT_SEARCH, DATE_RANGE, NUMERIC_RANGE) don't have options.
    const ds: DataSource | undefined = (item as { dataSource?: DataSource }).dataSource;
    if (!ds) {
      this.dispatchAction({ type: 'FIELD_PHASE', name, phase: 'READY' });
      return [];
    }

    const parents = Array.from(this.graph.parentsOf.get(name) ?? []);

    if (ds.type === 'static') {
      const { requestId } = this.registry.start(name);
      this.dispatchAction({ type: 'FIELD_OPTIONS_LOADING', name, requestId });
      this.dispatchAction({ type: 'FIELD_OPTIONS_RESOLVED', name, requestId, options: ds.options });
      return ds.options;
    }

    // async
    const parentValues = this.getParentValues(name);
    const cacheKey = ds.cacheKey
      ? ds.cacheKey(parentValues)
      : OptionsCache.defaultCacheKey(parents, parentValues);

    if (opts.useCache !== false) {
      const cached = this.cache.get(name, cacheKey);
      if (cached) {
        const { requestId } = this.registry.start(name);
        this.dispatchAction({ type: 'FIELD_OPTIONS_LOADING', name, requestId });
        this.dispatchAction({ type: 'FIELD_OPTIONS_RESOLVED', name, requestId, options: cached });
        return cached;
      }
    }

    const { signal, requestId } = this.registry.start(name);
    this.dispatchAction({ type: 'FIELD_OPTIONS_LOADING', name, requestId });
    try {
      const options = await ds.load(parentValues, signal);
      if (signal.aborted) return null;
      if (!this.registry.isCurrent(name, requestId)) return null;
      this.cache.set(name, cacheKey, options);
      this.dispatchAction({ type: 'FIELD_OPTIONS_RESOLVED', name, requestId, options });
      return options;
    } catch (err) {
      if (signal.aborted) return null;
      if (!this.registry.isCurrent(name, requestId)) return null;
      const message = err instanceof Error ? err.message : String(err);
      this.dispatchAction({ type: 'FIELD_OPTIONS_FAILED', name, requestId, error: message });
      return null;
    }
  }

  /**
   * Stale-value handling — called after options resolve.
   */
  private async handleStaleValueAndDefault(
    name: string,
    options: SelectOptionType[],
    parentChangePolicy?: DependsOnEntry['onParentChange'],
  ): Promise<void> {
    const item = this.getItem(name);
    if (!item) return;
    const field = this.state.fields[name];
    if (!field) return;

    const policy = parentChangePolicy ?? 'reset';
    const currentValue = field.value;
    const isValid = valueIsValidInOptions(item, currentValue, options);

    // Apply onParentChange policy if this is a cascade
    if (parentChangePolicy) {
      if (policy === 'reset' || policy === 'reset-to-default') {
        // Clear value
        this.dispatchAction({
          type: 'FIELD_SET_VALUE',
          name,
          value: canonicalEmpty(item),
          userTouched: false,
        });
        if (policy === 'reset') {
          // Don't re-run getDefaultValue
          this.dispatchAction({ type: 'FIELD_PHASE', name, phase: 'READY' });
          return;
        }
      } else if (policy === 'preserve-if-valid') {
        if (!isValid) {
          this.dispatchAction({
            type: 'FIELD_SET_VALUE',
            name,
            value: canonicalEmpty(item),
            userTouched: false,
          });
        } else {
          this.dispatchAction({ type: 'FIELD_PHASE', name, phase: 'READY' });
          return;
        }
      }
    } else if (!isValid) {
      // Init flow or non-cascade reload — apply stale handling logic per plan.
      if (!field.userTouched) {
        // Clear, then re-run default
        this.dispatchAction({
          type: 'FIELD_SET_VALUE',
          name,
          value: canonicalEmpty(item),
          userTouched: false,
        });
      } else if (item.clearable !== false) {
        this.dispatchAction({
          type: 'FIELD_SET_VALUE',
          name,
          value: canonicalEmpty(item),
          userTouched: false,
        });
      } else {
        // userTouched && !clearable
        const fallback = await this.resolveDefault(item, options);
        if (fallback === null || fallback === undefined) {
          this.dispatchAction({
            type: 'FIELD_OPTIONS_FAILED',
            name,
            requestId: field.requestId ?? '',
            error: `Stale value with no valid default for non-clearable field "${name}".`,
          });
          return;
        }
        this.dispatchAction({ type: 'FIELD_SET_VALUE', name, value: fallback });
        this.dispatchAction({ type: 'FIELD_PHASE', name, phase: 'READY' });
        return;
      }
    }

    // Re-read field state after possible value clear
    const next = this.state.fields[name];
    const needsDefault = next.value === undefined
      || next.value === null
      || (Array.isArray(next.value) && next.value.length === 0)
      || next.value === '';

    if (needsDefault && !next.userTouched) {
      this.dispatchAction({ type: 'FIELD_PHASE', name, phase: 'LOADING_DEFAULT' });
      const defaultValue = await this.resolveDefault(item, options);
      if (defaultValue !== null && defaultValue !== undefined) {
        this.dispatchAction({ type: 'FIELD_SET_VALUE', name, value: defaultValue });
      } else if (item.clearable === false) {
        this.dispatchAction({
          type: 'FIELD_ERROR',
          name,
          error: `No valid default available for non-clearable field "${name}".`,
        });
        return;
      }
    }
    this.dispatchAction({ type: 'FIELD_PHASE', name, phase: 'READY' });
  }

  /**
   * Initialize a single field — load options, then handle defaults.
   * Used during init flow.
   */
  private async initializeField(name: string): Promise<void> {
    this.txnIncrement();
    try {
      const item = this.getItem(name);
      if (!item) return;
      const options = await this.loadOptionsForField(name);
      if (!options) return;
      await this.handleStaleValueAndDefault(name, options);
    } finally {
      this.txnDecrement();
    }
  }

  /**
   * Cascade reload — for a field that just had a parent change.
   * Applies onParentChange policy, reloads options, runs handlers, then descends.
   */
  private async cascadeField(name: string, policy: DependsOnEntry['onParentChange']): Promise<void> {
    this.txnIncrement();
    try {
      const item = this.getItem(name);
      if (!item) return;
      const valueBefore = this.state.fields[name]?.value;

      const options = await this.loadOptionsForField(name, { useCache: true });
      if (!options) return;

      await this.handleStaleValueAndDefault(name, options, policy);

      // If value changed, cascade to direct dependents.
      const valueAfter = this.state.fields[name]?.value;
      if (!shallowEqualValue(valueBefore, valueAfter)) {
        const dependents = getDirectDependents(this.graph, name);
        await Promise.all(
          dependents.map((dep) => {
            const item2 = this.getItem(dep);
            const policyForDep = item2?.dependsOn?.find((d) => d.name === name)?.onParentChange ?? 'reset';
            return this.cascadeField(dep, policyForDep);
          }),
        );
      }
    } finally {
      this.txnDecrement();
    }
  }

  /**
   * Trigger a cascade transaction starting at `name`. `name` is assumed to have
   * been set already; this kicks off the dependents.
   */
  private async runCascadeFrom(name: string, options: OrchestratorOptions): Promise<void> {
    this.ensureTxnOpen(options.source, options.trigger ?? name, options.operation);
    if (options.skipCascade) return;
    const dependents = getDirectDependents(this.graph, name);
    if (dependents.length === 0) return;

    this.txnIncrement();
    try {
      await Promise.all(
        dependents.map((dep) => {
          const item = this.getItem(dep);
          const policy = item?.dependsOn?.find((d) => d.name === name)?.onParentChange ?? 'reset';
          return this.cascadeField(dep, policy);
        }),
      );
    } finally {
      this.txnDecrement();
    }
  }

  /** Items reconciliation: spawn init for newly-added fields. */
  private queueReconcileInit(newItems: FilterItemConfig[], previouslyKnown: Set<string>): void {
    const newFields = newItems.filter((i) => !previouslyKnown.has(i.name));
    if (newFields.length === 0) return;
    this.txnIncrement();
    this.ensureTxnOpen('imperative', undefined, 'itemsReconcile');
    Promise.all(newFields.map((i) => this.initializeField(i.name)))
      .catch(() => {})
      .finally(this.txnDecrement);
  }

  /**
   * Items reconciliation: static options content changed for kept fields.
   * Re-read options and apply stale-value handling (plan "Items prop
   * reconciliation"). `policy: undefined` gives init-flow semantics — valid
   * values kept, invalid cleared, defaults re-run for untouched empty fields —
   * and cascadeField descends to dependents only if the value actually moved.
   * Values untouched by the refresh emit no onChange (empty `affected` set is
   * silent).
   */
  private queueOptionsRefresh(names: string[]): void {
    if (names.length === 0) return;
    this.txnIncrement();
    this.ensureTxnOpen('imperative', names[0], 'itemsReconcile');
    Promise.all(names.map((name) => this.cascadeField(name, undefined)))
      .catch(() => {})
      .finally(this.txnDecrement);
  }

  /**
   * Run a "clear" or "reset" operation against a set of fields in one transaction.
   *
   * Plan Section "Cascade behavior for clear / clearAll / reset / resetAll":
   *   - Each field gets a target per `targetFor(item, op)`.
   *   - Targets are assigned in dep order (parents first).
   *   - Parent value changes open the standard cascade to dependents, but the
   *     dependent's operation-target wins over `onParentChange` policy
   *     (the operation has the final say on what the value becomes).
   *   - Pills hit `LOADING_OPTIONS` as their options reload; siblings stay interactive.
   *   - Single onChange after the whole cascade settles.
   */
  private runOperation(
    op: 'clear' | 'reset',
    fieldNames: string[],
    trigger: string | undefined,
    operation: FilterBarOperation,
  ): void {
    if (fieldNames.length === 0) return;
    this.txnIncrement();
    this.ensureTxnOpen('imperative', trigger, operation);

    // 1) Compute per-field targets up-front. Fields touched by this operation
    //    are tagged so cascade picks the operation target over onParentChange.
    const operationTargets = new Map<string, unknown | 'BASELINE'>();
    for (const n of fieldNames) {
      const item = this.getItem(n);
      if (!item) continue;
      operationTargets.set(n, targetFor(item, op));
    }

    (async () => {
      // 2) Apply targets in topological order so parents settle before dependents.
      const topo = this.state.itemsOrder.filter((n) => operationTargets.has(n));
      // Group by level (independent root fields can run in parallel).
      const orderedLevels = computeLevels(this.graph, topo);

      for (const level of orderedLevels) {
        await Promise.all(level.map(async (name) => {
          const item = this.getItem(name)!;
          const target = operationTargets.get(name);

          // Sync assignment for literal targets (canonical empty).
          if (target !== 'BASELINE') {
            this.dispatchAction({ type: 'FIELD_SET_VALUE', name, value: target, userTouched: false });
            return;
          }

          // Baseline: defaultValue if set (sync), else rerun getDefaultValue
          // against current options (async — may require options reload first
          // if parent changed but that happens in the cascade step below).
          const currentOptions = this.state.fields[name]?.options ?? [];
          const baseline = await this.resolveDefault(item, currentOptions);
          if ((baseline === null || baseline === undefined) && item.clearable === false) {
            this.dispatchAction({
              type: 'FIELD_ERROR',
              name,
              error: `No valid default available for non-clearable field "${name}".`,
            });
            return;
          }
          this.dispatchAction({
            type: 'FIELD_SET_VALUE',
            name,
            value: baseline ?? canonicalEmpty(item),
            userTouched: false,
          });
        }));
      }

      // 3) Cascade to dependents. operationTargets ensures children touched by
      //    this operation use their declared target, NOT onParentChange.
      const allDependents = new Set<string>();
      for (const n of fieldNames) {
        for (const d of getDescendants(this.graph, n)) allDependents.add(d);
      }
      // Strip out fields directly in this operation — they were handled above.
      for (const n of fieldNames) allDependents.delete(n);

      await Promise.all(
        Array.from(allDependents).map(async (dep) => {
          const depItem = this.getItem(dep);
          if (!depItem) return;
          // Reload options against the new parent values.
          const opts = await this.loadOptionsForField(dep, { useCache: true });
          if (!opts) return;
          // If this dependent ALSO has an operation target (clearAll/resetAll
          // touches it directly), apply that target over its new options.
          if (operationTargets.has(dep)) {
            const tgt = operationTargets.get(dep);
            if (tgt !== 'BASELINE') {
              this.dispatchAction({ type: 'FIELD_SET_VALUE', name: dep, value: tgt, userTouched: false });
            } else {
              const baseline = await this.resolveDefault(depItem, opts);
              if ((baseline === null || baseline === undefined) && depItem.clearable === false) {
                this.dispatchAction({
                  type: 'FIELD_ERROR',
                  name: dep,
                  error: `No valid default available for non-clearable field "${dep}".`,
                });
                return;
              }
              this.dispatchAction({
                type: 'FIELD_SET_VALUE',
                name: dep,
                value: baseline ?? canonicalEmpty(depItem),
                userTouched: false,
              });
            }
            return;
          }
          // Otherwise it's a normal cascade — apply the dependent's onParentChange.
          const parentEntry = depItem.dependsOn?.find((d) => fieldNames.includes(d.name));
          const policy = parentEntry?.onParentChange ?? 'reset';
          await this.handleStaleValueAndDefault(dep, opts, policy);
        }),
      );
    })().finally(this.txnDecrement);
  }

  // ==========================================================================
  // Init sequence
  // ==========================================================================

  private async runInit(runId: number): Promise<void> {
    const cancelled = (): boolean => !this.started || runId !== this.initRunId;
    // Config reads are call-time — the hook synced the latest before start().
    const { persistence, initialValues, values: controlledValues, initialVisibleFields } = this.config;

    // Plan Section "Initial value priority":
    //   1) Controlled `values` prop (always wins).
    //   2) Adapter `load()` result (clearable:false fields skip this).
    //   3) `initialValues` / `defaultValues` prop.
    //   4) `FilterItemBase.defaultValue` per field.
    //   5) `getDefaultValue` per field (runs after options load if still unset).
    //   6) null.
    const seededValues: FilterValues = {};
    let persistedLoaded: FilterValues | null = null;

    if (persistence) {
      try {
        const loaded = await Promise.resolve(persistence.load());
        if (loaded && !cancelled()) {
          persistedLoaded = loaded;
          this.lastPersisted = loaded;
        }
      } catch (err) {
        console.warn('[FilterBar] persistence.load failed:', err);
      }
    }
    if (cancelled()) return;

    for (const item of this.itemsProp) {
      const fromControlled = controlledValues
        ? (controlledValues as FilterValues)[item.name]
        : undefined;
      if (fromControlled !== undefined) {
        seededValues[item.name] = fromControlled;
        continue;
      }
      // Persistence — skip for `clearable: false` fields (a stale persisted
      // value would violate "must always have a valid value").
      if (
        persistedLoaded
        && item.clearable !== false
        && persistedLoaded[item.name] !== undefined
      ) {
        seededValues[item.name] = persistedLoaded[item.name];
        continue;
      }
      const fromInitial = initialValues
        ? (initialValues as FilterValues)[item.name]
        : undefined;
      if (fromInitial !== undefined) {
        seededValues[item.name] = fromInitial;
        continue;
      }
      if (item.defaultValue !== undefined) {
        seededValues[item.name] = item.defaultValue;
      } else {
        // Leave at canonical empty → getDefaultValue runs during init pass
        // (if supported & set).
        seededValues[item.name] = canonicalEmpty(item);
      }
    }

    // Dispatch seeded values
    for (const item of this.itemsProp) {
      this.dispatchAction({
        type: 'FIELD_SET_VALUE',
        name: item.name,
        value: seededValues[item.name],
        userTouched: false,
      });
    }

    // Set initial visibility
    const visible = computeInitialVisibility(this.itemsProp, initialVisibleFields);
    this.dispatchAction({ type: 'SET_VISIBLE', visibleFields: visible });

    if (cancelled()) return;

    // Run field initialization in topological levels (siblings in parallel,
    // dependents wait for parents).
    const levels = computeTopoLevels(this.graph);
    for (const level of levels) {
      if (cancelled()) return;
      await Promise.all(level.map((name) => this.initializeField(name)));
    }

    if (cancelled()) return;
    // Baseline the dirty-compare BEFORE SET_INITIALIZED so the first
    // initialized snapshot computes isDirty against the settled values.
    this.lastPersisted = currentValues(this.state);
    this.dispatchAction({ type: 'SET_INITIALIZED' });
  }

  // ==========================================================================
  // Persistence
  // ==========================================================================

  private scheduleAutoSave(): void {
    const { persistence, autoSave = true } = this.config;
    if (!persistence || !autoSave) return;
    if (!this.state.initialized) return;
    if (this.state.closedTxnCount === 0) return;

    const values = currentValues(this.state);
    const ms = persistence.debounceMs ?? 0;
    if (this.saveTimer) clearTimeout(this.saveTimer);

    // Value-neutral transactions don't persist: an options-content refresh
    // that invalidated nothing (or an A→B→A that nets to zero within the
    // debounce — its pending save was just cleared above) would write a
    // payload identical to the last save/load. This also keeps the plan's
    // "first save fires only after a post-init action" intact when option
    // lists resolve around init.
    if (shallowEqualValues(values, this.lastPersisted ?? {})) return;

    this.saveTimer = setTimeout(() => {
      Promise.resolve(persistence.save(values))
        .then(() => {
          this.lastPersisted = values;
          this.refreshDerived();
        })
        .catch((err) => console.warn('[FilterBar] persistence.save failed:', err));
    }, ms);
  }

  private buildPersistenceApi(): FilterBarPersistenceController {
    return {
      save: async () => {
        const adapter = this.config.persistence;
        if (!adapter) return;
        const values = currentValues(this.state);
        try {
          await Promise.resolve(adapter.save(values));
          this.lastPersisted = values;
          this.refreshDerived();
        } catch (err) {
          console.warn('[FilterBar] persistence.save failed:', err);
        }
      },
      load: async () => {
        const adapter = this.config.persistence;
        if (!adapter) return;
        try {
          const loaded = await Promise.resolve(adapter.load());
          if (loaded) {
            this.lastPersisted = loaded;
            this.ensureTxnOpen('imperative', undefined, 'persistenceLoad');
            for (const k of Object.keys(loaded)) {
              this.dispatchAction({ type: 'FIELD_SET_VALUE', name: k, value: loaded[k], userTouched: false });
            }
            // Close after sync seed
            queueMicrotask(() => {
              if (this.pendingTxn === 0 && this.state.transactionOpen) {
                this.dispatchAction({ type: 'CASCADE_CLOSE' });
              }
            });
            this.refreshDerived();
          }
        } catch (err) {
          console.warn('[FilterBar] persistence.load failed:', err);
        }
      },
      clear: async () => {
        const adapter = this.config.persistence;
        if (!adapter) return;
        try {
          if (adapter.clear) await Promise.resolve(adapter.clear());
          this.lastPersisted = null;
          // Wipe in-memory state too — apply the per-field clear rule across
          // every field, then mark the bar undirty.
          this.runOperation('clear', this.state.itemsOrder, undefined, 'clearAll');
          this.refreshDerived();
        } catch (err) {
          console.warn('[FilterBar] persistence.clear failed:', err);
        }
      },
    };
  }
}

/**
 * Narrow a public controller back to the store instance for the library's
 * internal components (per-field state subscriptions). Controllers are only
 * ever created by `useFilterBar`, so this is a checked downcast.
 */
export function toFilterBarStore<T extends FilterValues = FilterValues>(
  controller: FilterBarController<T>,
): FilterBarStore<T> {
  if (controller instanceof FilterBarStore) {
    return controller as FilterBarStore<T>;
  }
  throw new Error(
    '[FilterBar] The provided `controller` was not created by useFilterBar(). '
    + 'Custom controller objects are not supported.',
  );
}

// ---------- Module helpers ----------

function indexByName(items: FilterItemConfig[]): Record<string, FilterItemConfig> {
  const out: Record<string, FilterItemConfig> = {};
  for (const item of items) out[item.name] = item;
  return out;
}

function computeItemsSignature(items: FilterItemConfig[]): string {
  return items.map((i) => {
    const deps = (i.dependsOn ?? []).map((d) => `${d.name}~${d.onParentChange ?? 'reset'}`).join(',');
    return `${i.name}|${i.type}|${deps}`;
  }).join('||');
}

function collectStaticOptions(list: FilterItemConfig[]): Map<string, SelectOptionType[]> {
  const map = new Map<string, SelectOptionType[]>();
  for (const item of list) {
    const ds = (item as { dataSource?: DataSource }).dataSource;
    if (ds?.type === 'static') map.set(item.name, ds.options);
  }
  return map;
}

/** Identity-preserving Record rebuild — returns `prev` when content is unchanged. */
function preserveRecordIdentity<V>(
  prev: Record<string, V> | undefined,
  names: string[],
  read: (name: string) => V,
): Record<string, V> {
  const out: Record<string, V> = {};
  let changed = !prev || Object.keys(prev).length !== names.length;
  for (const n of names) {
    const v = read(n);
    out[n] = v;
    if (!changed && !Object.is(prev![n], v)) changed = true;
  }
  return changed ? out : prev!;
}

function valueIsValidInOptions(item: FilterItemConfig, value: unknown, options: SelectOptionType[]): boolean {
  if (item.type === 'SELECT') {
    if (value === null || value === undefined) return true;
    return flattenOptionValues(options).has(value as string | number);
  }
  if (item.type === 'SELECT_MULTI') {
    const arr = (value as Array<string | number> | undefined) ?? [];
    if (arr.length === 0) return true;
    const set = flattenOptionValues(options);
    return arr.every((v) => set.has(v));
  }
  return true;
}

/**
 * Per-field rule selector — given a field config and the operation
 * (`clear` vs `reset`), returns what the field's value should become.
 *
 * Returns `'BASELINE'` to signal "restore baseline" (orchestrator runs
 * resolveDefault), or a literal value (which may be `canonical empty`).
 */
function targetFor(item: FilterItemConfig, op: 'clear' | 'reset'): unknown | 'BASELINE' {
  if (op === 'reset') return 'BASELINE';
  // op === 'clear'
  return item.clearable === false ? 'BASELINE' : canonicalEmpty(item);
}

function flattenOptionValues(options: SelectOptionType[]): Set<string | number> {
  const out = new Set<string | number>();
  const walk = (nodes: SelectOptionType[]) => {
    for (const n of nodes) {
      if (n.value !== undefined) out.add(n.value);
      if (n.options) walk(n.options);
      if (n.children) walk(n.children);
    }
  };
  walk(options);
  return out;
}

function findOptionInTree(options: SelectOptionType[], value: unknown): SelectOptionType | undefined {
  for (const opt of options) {
    if (opt.value === value) return opt;
    if (opt.options) {
      const sub = findOptionInTree(opt.options, value);
      if (sub) return sub;
    }
    if (opt.children) {
      const sub = findOptionInTree(opt.children, value);
      if (sub) return sub;
    }
  }
  return undefined;
}

function computeTopoLevels(graph: DependencyGraph): string[][] {
  return computeLevels(graph, graph.order);
}

/**
 * Build topological levels (parents first, siblings parallel) over a SUBSET of
 * the graph. Edges into nodes outside `subset` are ignored — the subset is
 * scheduled as if those external parents had already settled.
 */
function computeLevels(graph: DependencyGraph, subset: string[]): string[][] {
  const subsetSet = new Set(subset);
  const inDegree = new Map<string, number>();
  for (const name of subset) {
    const parents = graph.parentsOf.get(name) ?? new Set<string>();
    let degree = 0;
    for (const p of parents) if (subsetSet.has(p)) degree += 1;
    inDegree.set(name, degree);
  }
  const remaining = new Set(subset);
  const levels: string[][] = [];
  while (remaining.size > 0) {
    const current: string[] = [];
    for (const n of remaining) {
      if ((inDegree.get(n) ?? 0) === 0) current.push(n);
    }
    if (current.length === 0) break;
    levels.push(current);
    for (const n of current) {
      remaining.delete(n);
      for (const dep of graph.dependentsOf.get(n) ?? []) {
        if (!subsetSet.has(dep)) continue;
        inDegree.set(dep, (inDegree.get(dep) ?? 0) - 1);
      }
    }
  }
  return levels;
}

function computeInitialVisibility(
  items: FilterItemConfig[],
  initialVisibleFields: string[] | undefined,
): string[] {
  if (initialVisibleFields) {
    // pinned fields always visible regardless
    const out = new Set(initialVisibleFields);
    for (const item of items) {
      if (item.pinned) out.add(item.name);
    }
    return Array.from(out);
  }
  // Default: show all fields. The + menu can remove non-pinned ones.
  return items.map((i) => i.name);
}
